import React, { useState } from 'react';
import { DollarSign, Truck, Package, CheckCircle2, Phone, Mail, ArrowRight, Clock, Shield, TrendingDown, Calculator } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useToast } from '../hooks/use-toast';
import { Helmet } from 'react-helmet-async';
import QuoteRequestForm from '../components/QuoteRequestForm';

const CheapShippingCanadaToIndiaPage = () => {
  const { toast } = useToast();

  const benefits = [
    {
      icon: DollarSign,
      title: "Best Price Guarantee",
      description: "We compare rates from multiple carriers including DHL, FedEx, UPS, and Purolator to ensure you always get the most competitive pricing for shipping to India."
    },
    {
      icon: TrendingDown,
      title: "Save Up to 75%",
      description: "Our bulk shipping partnerships and carrier negotiations allow us to offer rates up to 75% lower than standard retail courier prices."
    },
    {
      icon: Calculator,
      title: "Transparent Pricing",
      description: "No hidden fees or surprise charges. Get accurate quotes upfront with clear breakdowns of all costs before you ship."
    },
    {
      icon: Truck,
      title: "Multiple Carrier Options",
      description: "Choose from economy to express services based on your budget and timeline. We work with all major international carriers."
    },
    {
      icon: Clock,
      title: "Fast Delivery Times",
      description: "Even with affordable rates, we maintain fast delivery times. Economy shipments arrive in 10-15 days, express in 3-7 days."
    },
    {
      icon: Shield,
      title: "Reliable & Secure",
      description: "Affordable doesn't mean compromising on quality. Full tracking, insurance options, and customs clearance support included."
    }
  ];

  const steps = [
    {
      number: 1,
      title: "Get Free Quote",
      description: "Contact us with your shipment details. We'll compare rates from DHL, FedEx, UPS and provide you with the most affordable options."
    },
    {
      number: 2,
      title: "Choose Your Service",
      description: "Select between economy shipping for maximum savings or express service for faster delivery at competitive rates."
    },
    {
      number: 3,
      title: "Package Your Items",
      description: "We provide guidance on proper packaging to avoid dimensional weight charges and ensure safe transit to India."
    },
    {
      number: 4,
      title: "Drop Off or Schedule Pickup",
      description: "Use our partner drop-off locations across Canada or schedule a convenient pickup from your home or office."
    },
    {
      number: 5,
      title: "Track & Receive",
      description: "Monitor your shipment's progress with real-time tracking and rest assured it will reach any address in India safely."
    }
  ];

  const savingTips = [
    {
      tip: "Optimize Package Dimensions",
      description: "Use smaller boxes and remove excess packaging. Dimensional weight pricing means compact packages save money."
    },
    {
      tip: "Choose Economy Service",
      description: "If your shipment isn't urgent, economy options can save 40-60% compared to express services while still being reliable."
    },
    {
      tip: "Ship in Bulk",
      description: "Consolidate multiple items into one shipment. We offer special rates for shipments over 50kg with even better rates at 100kg+."
    },
    {
      tip: "Book During Off-Peak",
      description: "Avoid peak shipping seasons (November-December) when possible to get better rates and faster processing."
    },
    {
      tip: "Use Our Partner Locations",
      description: "Drop off at partner locations instead of requesting pickup to save on collection fees."
    },
    {
      tip: "Accurate Declaration",
      description: "Proper customs documentation prevents delays and additional charges at Indian customs."
    }
  ];

  const canadianCities = [
    'Toronto', 'Brampton', 'Mississauga', 'Etobicoke', 'Scarborough',
    'Markham', 'Surrey', 'Vancouver', 'Richmond', 'Calgary',
    'Edmonton', 'Halifax', 'Montreal', 'Ottawa', 'Winnipeg'
  ];

  const faqs = [
    {
      question: "How much does cheap shipping from Canada to India cost?",
      answer: "Our affordable rates start at $7.99/kg for economy shipments over 100kg. For regular parcels, rates typically range from $8-15/kg depending on weight, dimensions, and service selected. We compare DHL, FedEx, UPS, and other carriers to find you the best price. Contact us for an accurate quote based on your specific shipment."
    },
    {
      question: "Is cheap shipping to India reliable and safe?",
      answer: "Absolutely! Affordable pricing doesn't mean compromising on quality or reliability. We only work with established, trusted carriers like DHL, FedEx, and UPS. All shipments include tracking, and insurance options are available. We've successfully delivered thousands of parcels to India with excellent safety records."
    },
    {
      question: "How long does economy shipping to India take?",
      answer: "Standard parcel shipping from Canada to India typically takes 3-7 business days for delivery. Express document services deliver in 2-5 business days. Standard document delivery takes 7-9 business days. Delivery times may vary based on customs clearance and destination city in India."
    },
    {
      question: "Which carrier offers the cheapest rates to India?",
      answer: "The cheapest carrier varies depending on your shipment's weight, dimensions, origin, and destination. That's why we compare rates from DHL, FedEx, UPS, Purolator, and other carriers for every quote. We present you with the most affordable option that meets your timeline and service requirements."
    },
    {
      question: "Are there any hidden fees when shipping to India?",
      answer: "No hidden fees with Shippzy! We provide transparent, all-inclusive quotes upfront. Our pricing includes pickup (if applicable), transportation, and delivery. However, please note that customs duties and taxes imposed by Indian authorities are separate and the responsibility of the sender or recipient, as required by Indian customs regulations."
    },
    {
      question: "Can I get cheaper rates for large or bulk shipments?",
      answer: "Yes! We offer special discounted rates for bulk shipments. Shipments over 50kg qualify for reduced per-kg pricing, with even better rates for 100kg+ shipments (from $7.99/kg). If you're relocating or shipping multiple items, consolidating into one large shipment can result in significant savings."
    },
    {
      question: "How can I reduce my shipping costs to India?",
      answer: "Several ways to save: (1) Use compact packaging to minimize dimensional weight charges, (2) Choose economy service instead of express, (3) Consolidate multiple items into one shipment, (4) Drop off at partner locations instead of requesting pickup, (5) Ship during off-peak periods, and (6) Ensure accurate customs documentation to avoid delays and fees."
    },
    {
      question: "Do you offer door-to-door delivery with cheap rates?",
      answer: "Yes! Even our most affordable economy rates include door-to-door delivery service. We pick up from your location in Canada (or you can use drop-off locations) and deliver directly to any address in India. Full tracking is included so you can monitor your shipment every step of the way."
    }
  ];

  const [openFaq, setOpenFaq] = useState(null);

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <>
      <Helmet>
        <title>Cheap Shipping Canada to India | Affordable Courier – Shippzy</title>
        <meta name="description" content="Send parcels from Canada to India at affordable rates with Shippzy. Compare DHL, FedEx and UPS prices and choose the best international courier option." />
        <meta name="keywords" content="cheap shipping Canada to India, affordable courier Canada to India, cheap parcel delivery India, low cost shipping Canada India, best rates Canada to India, economy shipping India" />
        <link rel="canonical" href="https://www.shippzy.ca/cheap-shipping-canada-to-india" />
        
        <meta property="og:title" content="Cheap Shipping Canada to India | Affordable Courier – Shippzy" />
        <meta property="og:description" content="Send parcels from Canada to India at affordable rates. Compare DHL, FedEx, UPS prices and save up to 75%." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.shippzy.ca/cheap-shipping-canada-to-india" />
        
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "name": "Cheap Shipping from Canada to India",
            "provider": {
              "@type": "Organization",
              "name": "Shippzy Inc.",
              "telephone": "+1-437-616-0758",
              "email": "ship@shippzy.ca"
            },
            "areaServed": ["CA", "IN"],
            "serviceType": "International Shipping",
            "offers": {
              "@type": "Offer",
              "priceSpecification": {
                "@type": "PriceSpecification",
                "price": "7.99",
                "priceCurrency": "CAD",
                "unitText": "per kg",
                "minWeight": "100 kg minimum (India airport-to-airport)"
              }
            }
          })}
        </script>
        
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-white">
        <section className="bg-gradient-to-br from-gray-900 via-gray-800 to-black text-white py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl">
              <div className="inline-block bg-gradient-to-r from-red-600 to-yellow-500 text-white px-4 py-2 rounded-full text-sm font-semibold mb-6">
                💰 Save Up to 75% on Shipping
              </div>
              <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
                Cheap Shipping from Canada to India
              </h1>
              <p className="text-xl text-gray-300 leading-relaxed mb-8">
                Shippzy makes international shipping affordable without compromising on quality or reliability. We compare rates from leading carriers including DHL, FedEx, and UPS to ensure you always get the cheapest shipping option from Canada to India. Whether you're sending documents, gifts, or large parcels, our transparent pricing and bulk discounts help you save significantly on every shipment.
              </p>
              <div className="flex flex-wrap gap-4">
                <Button 
                  className="bg-gradient-to-r from-red-600 to-yellow-500 hover:from-red-700 hover:to-yellow-600 text-white px-8 py-6 text-lg"
                  onClick={() => document.getElementById('quote-form').scrollIntoView({ behavior: 'smooth' })}
                >
                  Get Cheap Quote Now
                </Button>
                <Button 
                  variant="outline" 
                  className="border-2 border-white text-white hover:bg-white hover:text-gray-900 px-8 py-6 text-lg"
                  onClick={() => window.location.href = 'tel:+14376160758'}
                >
                  Call: +1 (437) 616-0758
                </Button>
              </div>
            </div>
          </div>
        </section>

        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Why Shippzy Offers the Most Affordable Rates</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Our partnerships, bulk discounts, and carrier comparisons ensure you always get the best price for shipping to India.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {benefits.map((benefit, index) => {
                const IconComponent = benefit.icon;
                return (
                  <Card key={index} className="hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                    <CardContent className="pt-6">
                      <div className="w-14 h-14 bg-gradient-to-r from-red-600 to-yellow-500 rounded-lg flex items-center justify-center mb-4">
                        <IconComponent className="w-7 h-7 text-white" />
                      </div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-3">{benefit.title}</h3>
                      <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>

        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Carrier Rate Comparison</h2>
              <p className="text-xl text-gray-600">We work with all major carriers to find you the cheapest option</p>
            </div>

            <div className="grid md:grid-cols-4 gap-6 mb-12">
              {['DHL', 'FedEx', 'UPS', 'Purolator'].map((carrier, index) => (
                <Card key={index} className="text-center hover:shadow-lg transition-shadow">
                  <CardContent className="pt-8 pb-8">
                    <Package className="w-12 h-12 text-red-600 mx-auto mb-3" />
                    <h3 className="text-xl font-bold text-gray-900">{carrier}</h3>
                    <p className="text-sm text-gray-600 mt-2">Rates Compared</p>
                  </CardContent>
                </Card>
              ))}
            </div>

            <Card className="bg-gradient-to-r from-red-50 to-yellow-50 border-2 border-red-200">
              <CardContent className="pt-8">
                <div className="text-center">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">How We Keep Prices Low</h3>
                  <p className="text-lg text-gray-700 leading-relaxed max-w-3xl mx-auto">
                    We negotiate volume discounts with all major carriers and pass the savings directly to you. For every shipment, we automatically compare rates across DHL, FedEx, UPS, and Purolator to present you with the most affordable option that meets your delivery timeline. Our advanced logistics network and strategic partnerships allow us to offer rates up to 75% lower than retail courier prices.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        <section className="py-20 bg-gradient-to-br from-red-50 to-yellow-50">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">How to Reduce Your Shipping Costs</h2>
              <p className="text-xl text-gray-600">Expert tips to save even more on your Canada to India shipments</p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {savingTips.map((item, index) => (
                <Card key={index} className="hover:shadow-lg transition-shadow">
                  <CardContent className="pt-6">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-gradient-to-r from-red-600 to-yellow-500 rounded-full flex items-center justify-center flex-shrink-0">
                        <CheckCircle2 className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-gray-900 mb-2">{item.tip}</h3>
                        <p className="text-gray-600">{item.description}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        <section className="py-20 bg-white">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Simple 5-Step Shipping Process</h2>
              <p className="text-xl text-gray-600">Easy and affordable shipping from Canada to India</p>
            </div>

            <div className="space-y-6">
              {steps.map((step, index) => (
                <Card key={index} className="border-2 hover:border-red-300 transition-colors">
                  <CardContent className="pt-6">
                    <div className="flex gap-6 items-start">
                      <div className="w-12 h-12 bg-gradient-to-r from-red-600 to-yellow-500 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-2xl font-bold text-white">{step.number}</span>
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-gray-900 mb-2">{step.title}</h3>
                        <p className="text-gray-600">{step.description}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        <section className="py-20 bg-gray-50">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Cities We Serve Across Canada</h2>
              <p className="text-xl text-gray-600">Affordable shipping to India available from all major Canadian cities</p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              {canadianCities.map((city, index) => (
                <Card key={index} className="text-center hover:shadow-md transition-shadow">
                  <CardContent className="pt-4 pb-4">
                    <p className="font-semibold text-gray-900">{city}</p>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="mt-12 bg-gradient-to-r from-gray-50 to-gray-100 p-8 rounded-xl">
              <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center">Related Services</h3>
              <div className="flex flex-wrap gap-4 justify-center">
                <a href="/shipping-to-india-from-canada" className="text-red-600 hover:text-red-700 font-semibold underline">
                  Shipping to India from Canada
                </a>
                <span className="text-gray-400">•</span>
                <a href="/international-shipping-brampton" className="text-red-600 hover:text-red-700 font-semibold underline">
                  Shipping services in Brampton
                </a>
                <span className="text-gray-400">•</span>
                <a href="/international-courier-toronto" className="text-red-600 hover:text-red-700 font-semibold underline">
                  International courier Toronto
                </a>
                <span className="text-gray-400">•</span>
                <a href="/relocation-shipping-canada-to-india" className="text-red-600 hover:text-red-700 font-semibold underline">
                  Relocation shipping to India
                </a>
              </div>
            </div>
          </div>
        </section>

        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
              <p className="text-xl text-gray-600">Everything you need to know about cheap shipping to India</p>
            </div>

            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <Card key={index} className="border-2 hover:border-red-300 transition-colors cursor-pointer" onClick={() => setOpenFaq(openFaq === index ? null : index)}>
                  <CardContent className="pt-6">
                    <div className="flex items-start justify-between">
                      <h3 className="text-lg font-semibold text-gray-900 pr-4">{faq.question}</h3>
                      <ArrowRight className={`w-5 h-5 text-red-600 flex-shrink-0 transition-transform ${openFaq === index ? 'rotate-90' : ''}`} />
                    </div>
                    {openFaq === index && (
                      <p className="text-gray-600 mt-4 leading-relaxed">{faq.answer}</p>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        <section id="quote-form" className="py-20 bg-gradient-to-r from-red-600 to-yellow-500">
          <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
            <QuoteRequestForm />
          </div>
        </section>

        <section className="py-12 bg-gray-900 text-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h3 className="text-2xl font-bold mb-4">Start Shipping to India at Affordable Rates</h3>
            <p className="text-gray-300 mb-6">
              Get the cheapest shipping rates from Canada to India without compromising on quality. Compare DHL, FedEx, UPS prices and save up to 75%. Door-to-door delivery with full tracking and customs support included.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Button 
                className="bg-gradient-to-r from-red-600 to-yellow-500 hover:from-red-700 hover:to-yellow-600 text-white px-8 py-3"
                onClick={() => window.location.href = '/partner'}
              >
                Find Drop-Off Location
              </Button>
              <Button 
                variant="outline" 
                className="border-2 border-white text-white hover:bg-white hover:text-gray-900 px-8 py-3"
                onClick={() => window.location.href = '/'}
              >
                View All Services
              </Button>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default CheapShippingCanadaToIndiaPage;